import { FormEvent, useEffect, useState } from 'react';
import BookingAccountLayout from 'pages/booking/account/BookingAccountLayout';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import 'pages/booking/booking-auth-form.css';

type BillingAddress = { street: string; number: string; city: string; zip: string };
type Profile = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  language: string;
  billingAddress: BillingAddress | null;
};

const EMPTY_BILLING_ADDRESS: BillingAddress = { street: '', number: '', city: '', zip: '' };

/** Display and update a signed-in booking guest or customer's personal details. */
export default function AccountProfile() {
  const { request } = useApi();
  const { _t } = useLang();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [nextEmail, setNextEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const billingAddress = profile?.billingAddress ?? EMPTY_BILLING_ADDRESS;

  useEffect(() => {
    request<Profile>({ method: 'GET', url: '/reservations/account/profile' })
      .then(setProfile)
      .catch(() => setError(_t('Unable to load profile.')));
  }, [request, _t]);

  /** Save mutable profile fields other than the verified email address. */
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!profile) return;
    setError('');
    try {
      const { firstName, lastName, phoneNumber, language } = profile;
      const saved = await request<Profile>({
        method: 'PATCH',
        url: '/reservations/account/profile',
        data: { firstName, lastName, phoneNumber, language },
      });
      setProfile(saved);
      setMessage(_t('Personal details saved.'));
    } catch {
      setError(_t('Unable to save personal details.'));
    }
  };

  /** Save the billing address reused by booking payment forms. */
  const saveBillingAddress = async (event: FormEvent) => {
    event.preventDefault();
    if (!profile) return;
    setError('');
    try {
      const saved = await request<Profile>({
        method: 'PATCH',
        url: '/reservations/account/profile',
        data: { billingAddress },
      });
      setProfile(saved);
      setMessage(_t('Billing address saved.'));
    } catch {
      setError(_t('Unable to save billing address.'));
    }
  };

  /** Update one billing-address field while preserving the remaining fields. */
  const updateBillingAddress = (field: keyof BillingAddress, value: string) => {
    if (!profile) return;
    setProfile({ ...profile, billingAddress: { ...billingAddress, [field]: value } });
  };

  /** Send a verification link to a proposed replacement email address. */
  const changeEmail = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await request({ method: 'POST', url: '/reservations/account/email-change/request', data: { email: nextEmail } });
      setMessage(_t('Check the new address for a confirmation link.'));
      setNextEmail('');
    } catch {
      setError(_t('Unable to request the email change.'));
    }
  };

  return (
    <BookingAccountLayout>
      <div className="booking-auth-card">
        <h2>{_t('Personal details')}</h2>
        {profile && (
          <form onSubmit={save}>
            <label>
              {_t('First name')}
              <input
                value={profile.firstName}
                onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
              />
            </label>
            <label>
              {_t('Last name')}
              <input value={profile.lastName} onChange={(e) => setProfile({ ...profile, lastName: e.target.value })} />
            </label>
            <label>
              {_t('Phone number')}
              <input
                value={profile.phoneNumber || ''}
                onChange={(e) => setProfile({ ...profile, phoneNumber: e.target.value })}
              />
            </label>
            <label>
              {_t('Language')}
              <select value={profile.language} onChange={(e) => setProfile({ ...profile, language: e.target.value })}>
                <option value="sk">{_t('Slovak')}</option>
                <option value="cs">{_t('Czech')}</option>
                <option value="en">{_t('English')}</option>
              </select>
            </label>
            <button>{_t('Save')}</button>
          </form>
        )}
        {profile && (
          <form onSubmit={saveBillingAddress}>
            <h3>{_t('Billing address')}</h3>
            <label>
              {_t('Street')}
              <input
                required
                value={billingAddress.street}
                onChange={(e) => updateBillingAddress('street', e.target.value)}
              />
            </label>
            <label>
              {_t('House number')}
              <input
                required
                value={billingAddress.number}
                onChange={(e) => updateBillingAddress('number', e.target.value)}
              />
            </label>
            <label>
              {_t('City')}
              <input
                required
                value={billingAddress.city}
                onChange={(e) => updateBillingAddress('city', e.target.value)}
              />
            </label>
            <label>
              {_t('ZIP code')}
              <input
                required
                value={billingAddress.zip}
                onChange={(e) => updateBillingAddress('zip', e.target.value)}
              />
            </label>
            <button>{_t('Save billing address')}</button>
          </form>
        )}
        {profile && (
          <form onSubmit={changeEmail}>
            <h3>{_t('Change email')}</h3>
            <p>{profile.email}</p>
            <label>
              {_t('New email')}
              <input required type="email" value={nextEmail} onChange={(e) => setNextEmail(e.target.value)} />
            </label>
            <button>{_t('Send confirmation link')}</button>
          </form>
        )}
        {message && <p role="status">{message}</p>}
        {error && (
          <p className="booking-auth-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </BookingAccountLayout>
  );
}
